import { useRef } from 'react';
import { useFeatureFlags, FeatureFlags } from './useFeatureFlags';

export interface StableFlagsResult {
  flags: FeatureFlags;
  ready: boolean;
}

const stableStringify = (obj: FeatureFlags): string =>
  JSON.stringify(obj, Object.keys(obj).sort());

/**
 * Returns a referentially stable flags object that only changes when a flag
 * value actually changes. Uses sorted-key comparison because PostHog returns
 * keys in non-deterministic order across calls.
 */
export const useStableFlags = (): StableFlagsResult => {
  const { flags, ready } = useFeatureFlags();
  const prev = useRef(flags);
  const prevJson = useRef(stableStringify(flags));
  const readyRef = useRef(false);

  const json = stableStringify(flags);
  if (json !== prevJson.current) {
    prev.current = flags;
    prevJson.current = json;
  }

  if (ready) readyRef.current = true;

  return { flags: prev.current, ready: readyRef.current };
};
